import {
  FanOriginChattingUiApiService,
  type FanOriginDto,
  OfChatNotificationsService,
} from '@api'
import { attach, combine, createEffect, createStore } from 'effector'

import {
  getCategoryName,
  isCategoryValid,
} from '@/dialogs/EditFanOriginDialog/utils.ts'
import { $fan, $fanId } from '@/models/chattingUI/chats/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import { handleError } from '@/utils/error'

export const getFanCategoriesFx = attach({
  name: 'get fan categories',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (_: void, { creatorId }) => ({
    creatorId: creatorId!,
  }),
  effect: createEffect(
    FanOriginChattingUiApiService.getFanOriginsForCreatorChattingUi,
  ),
})

export const $isGettingCategoriesError = createStore<boolean>(false)
  .on(getFanCategoriesFx.failData, () => true)
  .reset(getFanCategoriesFx)

getFanCategoriesFx.failData.watch((error) => {
  handleError(
    error,
    'Something went wrong while getting categories',
    getFanCategoriesFx,
  )
})

export const createFanCategoryFx = attach({
  name: 'create fan category',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (name: string, { creatorId }) => ({
    creatorId: creatorId!,
    requestBody: { name },
  }),
  effect: createEffect(
    FanOriginChattingUiApiService.createFanOriginForCreatorChattingUi,
  ),
})

createFanCategoryFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while creating category')
})

export const editFanCategoryFx = attach({
  name: 'edit fan category',
  source: {
    creatorId: $creatorId,
  },
  mapParams: ({ id, name }: { name: string; id: number }, { creatorId }) => ({
    id,
    creatorId: creatorId!,
    requestBody: {
      name,
    },
  }),
  effect: createEffect(
    FanOriginChattingUiApiService.updateFanOriginForCreatorChattingUi,
  ),
})

editFanCategoryFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while editing category')
})

export const removeFanCategoryFx = attach({
  name: 'remove fan category',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (id: number, { creatorId }) => ({
    id,
    creatorId: creatorId!,
  }),
  effect: createEffect(
    async ({ id, creatorId }: { id: number; creatorId: number }) => {
      await FanOriginChattingUiApiService.deleteFanOriginForCreatorChattingUi({
        creatorId,
        id,
      })

      return id
    },
  ),
})

removeFanCategoryFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while deleting category')
})

export const updateFanOriginFx = attach({
  name: 'update fan origin',
  source: {
    creatorId: $creatorId,
    fanId: $fanId,
  },
  mapParams: (originId: number, { creatorId, fanId }) => ({
    creatorId: creatorId!,
    fanId: fanId!,
    requestBody: { originId },
  }),
  effect: createEffect(OfChatNotificationsService.setUserProperties),
})

updateFanOriginFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while updating category')
})

export const $fanCategories = createStore<Required<FanOriginDto>[]>([], {
  name: 'fan categories',
})
  .on(
    getFanCategoriesFx.doneData,
    (_, data) => data.filter(isCategoryValid) as Required<FanOriginDto>[],
  )
  .on(createFanCategoryFx.doneData, (store, data) =>
    isCategoryValid(data) ? [...store, data as Required<FanOriginDto>] : store,
  )
  .on(editFanCategoryFx.doneData, (store, data) =>
    store.map((category) =>
      category.id === data.id && !!data.name
        ? {
            ...category,
            name: data.name,
          }
        : category,
    ),
  )
  .on(removeFanCategoryFx.doneData, (store, idToRemove) =>
    store.filter(({ id }) => id !== idToRemove),
  )

export const $originName = combine($fan, $fanCategories, (fan, categories) => {
  const originId = fan?.originId

  return getCategoryName(originId!, categories)
})
